import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, EnvelopeSimple } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const errText = (e) => {
  const d = e.response?.data?.detail;
  return typeof d === "string" && !d.includes("<") ? d : "Something went wrong. Please try again shortly.";
};

const Card = ({ children }) => (
  <div data-testid="owner-recovery-page" className="mx-auto max-w-lg px-5 py-12 sm:px-6">
    <ShieldCheck size={44} weight="duotone" className="text-[#36858B]" />
    <h1 className="mt-3 font-serif text-4xl text-[#2C2A28]">Owner account recovery</h1>
    <div className="mt-6 rounded-2xl bg-white p-6 ring-1 ring-[#EBE4DA]">{children}</div>
  </div>
);

const Btn = (props) => (
  <button {...props} className="mt-5 w-full rounded-full bg-[#E06D53] px-6 py-4 text-base font-semibold text-white hover:bg-[#C95A42] disabled:opacity-50" />
);

export default function OwnerRecovery() {
  const [params] = useSearchParams();
  const token = params.get("t");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) api.get("/auth/owner-recovery").then((r) => setStatus(r.data)).catch(() => setStatus({ available: false }));
  }, [token]);

  const run = async (fn) => {
    setBusy(true); setError("");
    try { setDone((await fn()).data); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  const err = error && <p data-testid="owner-recovery-error" className="mt-4 text-sm text-[#A84A32]">{error}</p>;

  if (token) {
    return (
      <Card>
        {done ? (
          <div data-testid="owner-recovery-confirmed">
            <p className="font-semibold text-[#2C2A28]">Owner email confirmed: {done.email}</p>
            <p className="mt-2 text-[#4A4540]">{done.reset_link_sent
              ? "We've emailed a password reset link to that address. Open it to choose your new password. It expires in 30 minutes."
              : "We couldn't send the password reset email just now. Use Forgot password on the sign-in page with username admin."}</p>
          </div>
        ) : (
          <>
            <p className="text-[#4A4540]">Confirm that this inbox should become the owner email for username <strong>admin</strong>. Your diary, owner role and payment settings stay exactly the same.</p>
            <Btn data-testid="owner-recovery-confirm-btn" disabled={busy} onClick={() => run(() => api.post("/auth/owner-recovery/confirm", { token }))}>
              {busy ? "Confirming…" : "Confirm owner email"}
            </Btn>
            {err}
          </>
        )}
      </Card>
    );
  }

  return (
    <Card>
      {!status ? <p className="text-[#8A7F76]">Checking…</p> : !status.available ? (
        <p data-testid="owner-recovery-unavailable" className="text-[#4A4540]">Owner recovery isn't needed for this site. Use Forgot password on the sign-in page.</p>
      ) : done ? (
        <p data-testid="owner-recovery-sent" className="flex gap-2 text-[#4A4540]"><EnvelopeSimple size={22} className="shrink-0 text-[#36858B]" /> Verification link sent to {done.target}. Open it within 30 minutes to confirm.</p>
      ) : (
        <>
          <p className="text-[#4A4540]">We'll send a one-time verification link to <strong data-testid="owner-recovery-target">{status.target}</strong>. Nothing changes until you open it and confirm.</p>
          <Btn data-testid="owner-recovery-send-btn" disabled={busy} onClick={() => run(() => api.post("/auth/owner-recovery/start"))}>
            {busy ? "Sending…" : "Send verification link"}
          </Btn>
          {err}
        </>
      )}
    </Card>
  );
}
